import { Injectable, signal, computed } from '@angular/core';
import { Feedback } from './satisfaction.service';

@Injectable({
  providedIn: 'root'
})
export class FeedbackSelectionService {
  private selectedFeedbacks = signal<Feedback[]>([]);

  // Getters
  selected = this.selectedFeedbacks.asReadonly();
  
  count = computed(() => this.selectedFeedbacks().length);
  
  isEmpty = computed(() => this.selectedFeedbacks().length === 0);

  // Actions
  select(feedback: Feedback, force = false) {
    if (!force && !this.canSelect(feedback)) return;
    
    const current = this.selectedFeedbacks();
    if (!this.isSelected(feedback)) {
      this.selectedFeedbacks.set([...current, feedback]);
    }
  }

  forceSelect(feedback: Feedback) {
    this.select(feedback, true);
  }

  deselect(feedback: Feedback) {
    const current = this.selectedFeedbacks();
    this.selectedFeedbacks.set(current.filter(f => f.feedback_id !== feedback.feedback_id));
  }

  toggle(feedback: Feedback) {
    if (this.isSelected(feedback)) {
      this.deselect(feedback);
    } else {
      this.select(feedback);
    }
  }

  selectAll(feedbacks: Feedback[]) {
    const selectable = feedbacks.filter(f => this.canSelect(f));
    this.selectedFeedbacks.set(selectable);
  }

  clear() {
    this.selectedFeedbacks.set([]);
  }

  // Helpers
  isSelected(feedback: Feedback): boolean {
    return this.selectedFeedbacks().some(f => f.feedback_id === feedback.feedback_id);
  }

  canSelect(feedback: Feedback): boolean {
    return feedback.wants_reply && !!feedback.contact_email && !feedback.replied_at;
  }

  isAllSelected(feedbacks: Feedback[]): boolean {
    const selectable = feedbacks.filter(f => this.canSelect(f));
    return selectable.length > 0 && selectable.every(f => this.isSelected(f));
  }

  isSomeSelected(feedbacks: Feedback[]): boolean {
    const selectable = feedbacks.filter(f => this.canSelect(f));
    const selectedCount = selectable.filter(f => this.isSelected(f)).length;
    return selectedCount > 0 && selectedCount < selectable.length;
  }
}